import { useCallback, useEffect, useRef, useState } from "react"

import { Modal } from "@/components/Modal"
import ShareButton from "@/components/ShareButton"
import StrokeText from "@/components/StrokeText"
import Toast from "@/components/Toast"
import { drawQRCode, useShareChannels } from "@/hooks"
import { i18n } from "@/i18n"
import { useRoleInfo } from "@/user/session"
import { drawRecallShareImage } from "./helper"

import styles from "./index.module.scss"

interface Props {
    open: boolean
    onCancel: () => void
    nickname?: string
    code?: string
    cg?: string
    onShareComplete?: () => void
}

const PreviewModal: React.FC<Props> = (props) => {
    const { open, onCancel, nickname, code, cg, onShareComplete } = props

    const user = useRoleInfo()
    const displayNickname = `Dr.${nickname || i18n.getRecall("share.default")}`

    const [qrCodeUrl, setQrCodeUrl] = useState("")
    const [imageSrc, setImageSrc] = useState("")
    const imageRef = useRef<HTMLImageElement>(null)
    const shareImageRequestRef = useRef(0)

    const generateQRCode = useCallback(async () => {
        if (!open) {
            setQrCodeUrl("")
            return
        }

        const qrCanvas = await drawQRCode(user?.uid, true)
        setQrCodeUrl(qrCanvas.toDataURL("image/png"))
    }, [user?.uid, open])

    const generateShareImage = useCallback(async () => {
        if (!open || !cg || !qrCodeUrl) {
            setImageSrc("")
            return
        }

        const currentRequestId = shareImageRequestRef.current + 1
        shareImageRequestRef.current = currentRequestId

        try {
            const { base64 } = await drawRecallShareImage({
                cg,
                qrCodeUrl,
                nickname: displayNickname,
                code,
            })

            if (shareImageRequestRef.current === currentRequestId) {
                setImageSrc(base64)
            }
        } catch {
            if (shareImageRequestRef.current === currentRequestId) {
                setImageSrc("")
            }
        }
    }, [cg, code, displayNickname, open, qrCodeUrl])

    const download = useCallback(async () => {
        if (!imageSrc) {
            Toast.message("download error")
            return
        }

        try {
            const link = document.createElement("a")
            link.href = imageSrc
            link.download = `${i18n.getRecall("share.title")}.png`
            document.body.append(link)
            link.click()
            link.remove()
        } catch {
            Toast.message("download error")
        }
    }, [imageSrc])

    const { channels, onClickChannel } = useShareChannels({
        download,
        isRecall: true,
        imageRef,
        imageSrc,
        code,
        onShareComplete,
    })

    useEffect(() => {
        generateQRCode()
    }, [generateQRCode])

    useEffect(() => {
        generateShareImage()
    }, [generateShareImage])

    return (
        <Modal
            open={open}
            onCancel={onCancel}
            closeButtonClassName={styles.close}
            contentClassName={styles.preview}
            showCancelBtn={false}
            showOkBtn={false}
        >
            {cg && (
                <div className={styles.cg}>
                    <img src={cg} alt="cg" className={styles.cgImage} />
                </div>
            )}
            <img src={qrCodeUrl} alt="qrcode" className={styles.qrcode} />
            <div className={styles.nickname}>
                <StrokeText
                    text={displayNickname}
                    fontWeight="bold"
                    fontSize={30}
                    fillColor="#6c461d"
                    borderWidth={3}
                    borderColor="#fffaed"
                    offsetY={0}
                    padding={3}
                    maxWidth={462}
                    minFontSize={22}
                    className={styles.text}
                />
            </div>
            {code && (
                <div className={styles.code}>
                    <StrokeText
                        text={code}
                        fontFamily="HuXiaoBo-WeiWuSong-Jian"
                        fontSize={34}
                        fillColor="#ffffff"
                        borderWidth={2}
                        borderColor="#946e0f"
                        offsetY={0}
                        padding={2}
                        maxWidth={462}
                        minFontSize={26}
                    />
                </div>
            )}
            {imageSrc && (
                <img
                    ref={imageRef}
                    src={imageSrc}
                    alt={i18n.getRecall("share.title")}
                    decoding="sync"
                    loading="eager"
                    className={styles.shareImage}
                />
            )}
            <button
                type="button"
                className={styles.button}
                onClick={() => {
                    void onClickChannel("download")
                }}
            >
                <StrokeText
                    text={i18n.getRecall("share.save")}
                    fontFamily="Georgia-Bold"
                    fontSize={33}
                    fillColor="#fff"
                    borderWidth={4}
                    borderColor="#564426"
                    offsetY={0}
                    padding={4}
                    baselineAligned
                    className={styles.text}
                />
            </button>
            <div className={styles.shareButtons}>
                {channels.map((key) => (
                    <ShareButton
                        key={key}
                        type={key}
                        onClick={() => onClickChannel(key)}
                    />
                ))}
            </div>
        </Modal>
    )
}

export default PreviewModal
